import {
  Link,
  type LinkProps,
  createFileRoute,
  linkOptions,
} from "@tanstack/react-router";
import {
  BookOpen,
  Briefcase,
  Building2,
  ClipboardList,
  GraduationCap,
  Layers,
  Lightbulb,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihLandingHero } from "~/components/cih/landing-hero";
import { CihReveal } from "~/components/cih/reveal";
import { Button } from "~/components/ui/button";
import { getGroupedCCOs } from "~/lib/ccos";
import { getArticlesIndexFn } from "~/lib/functions/cih/articles";
import { getPublishedChallengesFn } from "~/lib/functions/cih/challenges";
import { getPublishedFrameworksFn } from "~/lib/functions/cih/frameworks";
import { getPublishedGuidesFn } from "~/lib/functions/cih/guides";
import { getPublishedPlaybooksFn } from "~/lib/functions/cih/playbooks";
import { getAllPublishedCtps } from "~/lib/functions/ctps";
import { BRAND_URL } from "~/lib/shared/brand";
import { countGroupedCCOs } from "~/lib/shared/ccos-count";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

const CANONICAL_URL = `${BRAND_URL}/coaching-intelligence`;

/**
 * A single hub tile. `link` is built with `linkOptions` so its destination is
 * verified against the route tree at compile time (all seven links resolve or
 * type-check fails). `emphasized` marks the lead tile of a group.
 */
type HubTile = {
  title: string;
  description: string;
  link: LinkProps;
  icon: LucideIcon;
  count: number;
  /** Singular noun; pluralized with a plain "s". */
  countLabel: string;
  emphasized?: boolean;
};

/** One of the three labelled "doors" of the hub. */
type HubGroup = {
  id: string;
  title: string;
  signpost: ReactNode;
  tiles: HubTile[];
};

export const Route = createFileRoute("/_public/coaching-intelligence")({
  staticData: { title: "Coaching Intelligence" },
  staleTime: 6 * 60 * 60 * 1000, // 6 hours
  loader: async () => {
    const [
      challenges,
      frameworks,
      ctps,
      ccoDirectory,
      articles,
      guides,
      playbooks,
    ] = await Promise.all([
      getPublishedChallengesFn(),
      getPublishedFrameworksFn(),
      getAllPublishedCtps(),
      getGroupedCCOs(),
      getArticlesIndexFn(),
      getPublishedGuidesFn(),
      getPublishedPlaybooksFn(),
    ]);

    return {
      counts: {
        challenges: challenges.length,
        frameworks: frameworks.length,
        trainingPrograms: ctps.length,
        // Counts leaf CCOs across both levels of the type → subcategory map,
        // over the same type list /organizations renders. Inlining this is what
        // produced the "2 organizations" bug — see countGroupedCCOs.
        organizations: countGroupedCCOs(ccoDirectory.organizations),
        articles: articles.length,
        guides: guides.length,
        playbooks: playbooks.length,
      },
    };
  },
  head: () => ({
    ...createBrandMeta({
      title: "Coaching Intelligence | Dream Coach Match",
      description:
        "The knowledge layer of Dream Coach Match — guidance for life's challenges, evidence-based frameworks, coach training programs, certifying organizations, and expert articles.",
      ogUrl: CANONICAL_URL,
      canonical: CANONICAL_URL,
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          createBreadcrumbStructuredData([
            { name: "Home", url: BRAND_URL },
            { name: "Coaching Intelligence", url: CANONICAL_URL },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Coaching Intelligence",
          description:
            "The knowledge layer of Dream Coach Match — guidance for what you're facing, the frameworks and institutions behind the profession, and everything for building a coaching practice.",
          url: CANONICAL_URL,
          isPartOf: { "@type": "WebSite", url: BRAND_URL },
        }),
      },
    ],
  }),
  component: CoachingIntelligencePage,
});

function CoachingIntelligencePage() {
  const { counts } = Route.useLoaderData();

  // Three labelled doors, mirroring the site nav (Guidance · The Profession ·
  // For Coaches). Same seven tiles, same links, same copy as before — only the
  // grouping and section headers are new. Order within each group is fixed;
  // the lead tile comes first.
  const groups: HubGroup[] = [
    {
      id: "seeking-help",
      title: "For people seeking help",
      signpost: (
        <>
          Start here if you&rsquo;re navigating something hard, or take the{" "}
          <Link
            to="/wheel-of-life"
            className="font-medium text-violet-700 underline decoration-violet-200 underline-offset-2 hover:text-violet-900"
          >
            Wheel of Life assessment
          </Link>{" "}
          to see which area of your life needs attention first.
        </>
      ),
      tiles: [
        {
          title: "Guidance for what you're facing",
          description:
            "Burnout, career crossroads, leadership pressure, relationships — direct, expert-written guidance for life's biggest challenges.",
          link: linkOptions({ to: "/coaching-for" }),
          icon: Lightbulb,
          count: counts.challenges,
          countLabel: "challenge",
          emphasized: true,
        },
        {
          title: "Articles",
          description:
            "Practical, in-depth articles written by credentialed coaches — not content-farm filler.",
          link: linkOptions({ to: "/articles" }),
          icon: BookOpen,
          count: counts.articles,
          countLabel: "article",
        },
      ],
    },
    {
      id: "the-profession",
      title: "The profession",
      signpost:
        "The standards, methods, and institutions behind credible coaching.",
      tiles: [
        {
          title: "Frameworks",
          description:
            "The evidence-based frameworks, methodologies, and protocols professional coaches actually use.",
          link: linkOptions({ to: "/frameworks" }),
          icon: Layers,
          count: counts.frameworks,
          countLabel: "framework",
        },
        {
          title: "Training programs",
          description:
            "The programs that train and certify coaches — what they teach and which coaches hold them.",
          link: linkOptions({ to: "/training-programs" }),
          icon: GraduationCap,
          count: counts.trainingPrograms,
          countLabel: "program",
        },
        {
          title: "Organizations",
          description:
            "The accreditation bodies and institutions that set the standards of the coaching profession.",
          link: linkOptions({ to: "/organizations" }),
          icon: Building2,
          count: counts.organizations,
          countLabel: "organization",
        },
      ],
    },
    {
      id: "for-coaches",
      title: "For coaches",
      signpost: "Everything for building and running your practice.",
      tiles: [
        {
          title: "Business guides",
          description:
            "Tactical, stage-matched guides for coaches building their practice — client acquisition, pricing, systems, and scaling.",
          link: linkOptions({ to: "/coaching-business" }),
          icon: Briefcase,
          count: counts.guides,
          countLabel: "guide",
        },
        {
          title: "Playbooks",
          description:
            "Step-by-step playbooks coaches can put to work right away to grow and run their practice.",
          link: linkOptions({ to: "/coaching-business/playbooks" }),
          icon: ClipboardList,
          count: counts.playbooks,
          countLabel: "playbook",
        },
      ],
    },
  ];

  // Hero stat row. Zero-count cells are dropped; the row hides entirely when
  // fewer than three remain (mirrors buildStatStrip's policy).
  const stats = [
    { value: counts.challenges, label: "Challenges covered" },
    { value: counts.frameworks, label: "Frameworks" },
    { value: counts.trainingPrograms, label: "Training programs" },
    { value: counts.articles, label: "Expert articles" },
  ].filter((s) => s.value > 0);

  return (
    <>
      <CihLandingHero
        accent="violet"
        eyebrow="Coaching Intelligence"
        title="The knowledge layer of coaching"
        lede="Everything we know about coaching, organized: guidance for what you're facing, the frameworks behind it, and the programs and organizations that shape the profession."
        cta={
          <>
            <Link to="/discovery">
              <Button variant="primary" size="lg">
                Find your coach →
              </Button>
            </Link>
            <a href="#for-coaches">
              <Button variant="default" size="lg">
                I&rsquo;m a coach
              </Button>
            </a>
          </>
        }
      >
        {stats.length >= 3 ? (
          <div className="mx-auto mt-14 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="font-heading text-3xl font-medium tracking-tight text-gray-900 tabular-nums md:text-4xl">
                  {stat.value}
                </div>
                <div className="mt-1.5 text-[10px] font-medium tracking-[0.22em] text-gray-500 uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CihLandingHero>

      {groups.map((group, groupIndex) => (
        <section
          key={group.id}
          id={group.id}
          className="mx-auto max-w-7xl scroll-mt-24 px-4 py-12 md:py-16 xl:px-0"
        >
          <CihReveal>
            <div className="flex items-baseline gap-4">
              <span className="font-heading text-sm font-medium text-violet-400 tabular-nums">
                0{groupIndex + 1}
              </span>
              <h2 className="font-heading text-2xl font-medium tracking-tight text-gray-900 md:text-3xl">
                {group.title}
              </h2>
            </div>
            <p className="mt-2 pl-9 text-base leading-normal text-gray-500">
              {group.signpost}
            </p>
          </CihReveal>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
            {group.tiles.map((tile, tileIndex) => (
              <CihReveal
                key={tile.title}
                delay={tileIndex * 0.06}
                className={tile.emphasized ? "sm:col-span-2" : ""}
              >
                <Link
                  {...tile.link}
                  className={`group flex h-full flex-col gap-3 rounded-2xl border p-7 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:shadow-md md:p-8 ${
                    tile.emphasized
                      ? "border-violet-100 bg-gradient-to-br from-violet-50/80 via-white to-white hover:border-violet-200"
                      : "border-gray-100 bg-white hover:border-gray-200"
                  }`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 transition-colors group-hover:bg-violet-100">
                    <tile.icon className="h-5 w-5 text-violet-700" />
                  </div>
                  <h3
                    className={`font-heading font-medium tracking-tight text-gray-900 ${
                      tile.emphasized
                        ? "text-2xl md:text-3xl"
                        : "text-xl md:text-2xl"
                    }`}
                  >
                    {tile.title}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed text-gray-500 ${
                      tile.emphasized ? "max-w-xl md:text-base" : ""
                    }`}
                  >
                    {tile.description}
                  </p>
                  {tile.count > 0 ? (
                    <p className="text-xs font-medium tracking-wider text-gray-500 uppercase">
                      {tile.count} {tile.countLabel}
                      {tile.count === 1 ? "" : "s"}
                    </p>
                  ) : null}
                  <span className="mt-auto pt-3 text-sm font-medium text-gray-900">
                    Explore{" "}
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-0.5">
                      →
                    </span>
                  </span>
                </Link>
              </CihReveal>
            ))}
          </div>
        </section>
      ))}

      <CihClosingCta
        kicker="The knowledge layer"
        headline="Knowledge is the start."
        sub="Whether you're looking for a coach or building your own practice, there's a next step from here."
        cta={
          <div className="flex flex-col gap-3 sm:flex-row md:flex-col lg:flex-row">
            <Link to="/discovery">
              <Button variant="primary" size="lg">
                Find your coach →
              </Button>
            </Link>
            <Link to="/page/$name" params={{ name: "coach" }}>
              <Button variant="default" size="lg">
                Join as a coach →
              </Button>
            </Link>
          </div>
        }
      />
    </>
  );
}
